import { ArrowRight, Shield, Users, TrendingUp, Calculator, PieChart, Award, CheckCircle, Star, BarChart, Clock, Target } from 'lucide-react';
import { Link } from 'react-router-dom';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section - Video-style with overlay */}
      <section className="relative h-[90vh] flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/7993568/pexels-photo-7993568.jpeg?w=1920&q=85" 
            alt="Professional wealth management consultation"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 via-blue-800/85 to-transparent"></div>
        </div>
        
        {/* Hero Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <div className="mb-6">
              <span className="inline-block bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-semibold tracking-wide">
                TRUSTED SINCE 1996 • 5000+ SATISFIED CLIENTS
              </span>
            </div>
            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Build Wealth.<br/>
              <span className="text-blue-300">Secure Future.</span>
            </h1>
            <p className="text-xl lg:text-2xl text-blue-100 mb-8 leading-relaxed">
              Expert investment guidance for Indians and NRIs. Transform systematic savings into lasting wealth through proven strategies and professional portfolio management.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/book-consultation"
                className="bg-white text-blue-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-50 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-xl"
              >
                Get Free Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/calculator"
                className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-4 rounded-lg font-bold text-lg transition-all inline-flex items-center justify-center"
              >
                <Calculator className="mr-2 h-5 w-5" />
                Calculate Returns
              </Link>
            </div>
          </div>
        </div>
        
        {/* Trust Indicators - Floating */}
        <div className="absolute bottom-8 left-0 right-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-3 gap-6 bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
              <div className="text-center">
                <div className="text-3xl font-bold text-white">₹500Cr+</div>
                <div className="text-blue-200 text-sm">Assets Under Management</div>
              </div>
              <div className="text-center border-l border-r border-white/30">
                <div className="text-3xl font-bold text-white">25+ Years</div>
                <div className="text-blue-200 text-sm">Experience & Trust</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white">SEBI Registered</div>
                <div className="text-blue-200 text-sm">Licensed Advisor</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition - Clean Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Why Choose Rumtek Finvest?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your trusted partner in wealth creation with proven expertise and personalized approach
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl hover:shadow-2xl transition-shadow bg-gradient-to-br from-blue-50 to-white">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">SEBI Registered</h3>
              <p className="text-gray-600 leading-relaxed">
                Fully licensed and regulated by SEBI, NSE, BSE, and MCX. Your investments are in safe, professional hands.
              </p>
            </div>
            
            <div className="text-center p-8 rounded-2xl hover:shadow-2xl transition-shadow bg-gradient-to-br from-green-50 to-white">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Target className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Personalized Strategy</h3>
              <p className="text-gray-600 leading-relaxed">
                Customized investment plans based on your goals, risk appetite, and financial timeline.
              </p>
            </div>
            
            <div className="text-center p-8 rounded-2xl hover:shadow-2xl transition-shadow bg-gradient-to-br from-purple-50 to-white">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">25+ Years Legacy</h3>
              <p className="text-gray-600 leading-relaxed">
                Established in 1996, we bring decades of market expertise and proven track record.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Philosophy - Visual Split Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              The Wealth Creation Philosophy
            </h2>
            <p className="text-xl text-gray-600">
              A perfect synergy of disciplined saving and strategic investing
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* The ART */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1659355894117-0ae6f8f28d0b?w=800&q=85" 
                  alt="Professional analyzing financial documents"
                  className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-8">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                    A
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">The ART</h3>
                </div>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Save first, spend later. The discipline of setting aside money every month before expenses creates the foundation for wealth. It's not about how much you earn, but how much you save.
                </p>
                <Link 
                  to="/about" 
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold text-lg group"
                >
                  Learn More 
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-2 transition-transform" />
                </Link>
              </div>
            </div>
            
            {/* The SCIENCE */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/8353821/pexels-photo-8353821.jpeg?w=800&q=85" 
                  alt="Wealth management consultation"
                  className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-8">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                    S
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">The SCIENCE</h3>
                </div>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Strategic asset allocation. Invest your savings in a diversified portfolio of equity, debt, and alternative assets. Professional management and rebalancing maximize returns.
                </p>
                <Link 
                  to="/services" 
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold text-lg group"
                >
                  Explore Services 
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-2 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wealth Creation Calculator - Interactive Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              Your Path To Financial Freedom
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              See how systematic investing of ₹1,00,000 per month can create a life-changing corpus in just 8 years
            </p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 lg:p-12 border border-white/20">
            <div className="text-center mb-8">
              <div className="text-2xl font-bold mb-2">Monthly Investment</div>
              <div className="text-5xl font-bold text-yellow-400">₹1,00,000</div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-8 text-center">
                <div className="text-sm uppercase tracking-wide mb-2">Conservative @ 8%</div>
                <div className="text-4xl font-bold mb-4">₹1.35 Cr</div>
                <div className="text-sm mb-4">Corpus after 8 years</div>
                <div className="border-t border-white/30 pt-4">
                  <div className="text-sm">Monthly Income</div>
                  <div className="text-3xl font-bold">₹89,841</div>
                  <div className="text-xs mt-2">For life, while corpus keeps growing</div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-8 text-center">
                <div className="text-sm uppercase tracking-wide mb-2">Growth @ 10%</div>
                <div className="text-4xl font-bold mb-4">₹1.47 Cr</div>
                <div className="text-sm mb-4">Corpus after 8 years</div>
                <div className="border-t border-white/30 pt-4">
                  <div className="text-sm">Monthly Income</div>
                  <div className="text-3xl font-bold">₹1,22,833</div>
                  <div className="text-xs mt-2">For life, while corpus keeps growing</div>
                </div>
              </div>
            </div>
            
            <div className="text-center bg-yellow-400/20 rounded-xl p-6 mb-8">
              <p className="text-lg font-semibold mb-2">THIS IS MATHEMATICS.</p>
              <p className="text-sm text-blue-100">
                Assumptions: Corpus stays intact. Monthly withdrawals are earnings only. Amount keeps growing at same rate.
              </p>
            </div>
            
            <div className="text-center">
              <Link
                to="/calculator"
                className="bg-white text-blue-900 px-10 py-4 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all transform hover:scale-105 inline-flex items-center shadow-2xl"
              >
                <Calculator className="mr-2 h-6 w-6" />
                Calculate Your Wealth Journey
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Client Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real results from real people. Join 5000+ satisfied investors who achieved their financial dreams.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-shadow">
              <div className="flex items-center space-x-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-700 mb-6 text-lg leading-relaxed italic">
                "Started with ₹25,000 monthly SIP 8 years ago. Today my portfolio is worth ₹1.2 crores! Rumtek's systematic approach really works."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                  RK
                </div>
                <div>
                  <div className="font-bold text-gray-900">Rajesh Kumar</div>
                  <div className="text-sm text-gray-600">IT Professional, Mumbai</div>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-shadow">
              <div className="flex items-center space-x-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-700 mb-6 text-lg leading-relaxed italic">
                "Their retirement planning helped me retire at 55 with a comfortable corpus. Expert guidance made all the difference."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                  PS
                </div>
                <div>
                  <div className="font-bold text-gray-900">Priya Sharma</div>
                  <div className="text-sm text-gray-600">Entrepreneur, Bangalore</div>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-shadow">
              <div className="flex items-center space-x-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-700 mb-6 text-lg leading-relaxed italic">
                "PMS service has given me 16% annual returns consistently. Their research and stock selection is outstanding."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                  AM
                </div>
                <div>
                  <div className="font-bold text-gray-900">Anil Mehta</div>
                  <div className="text-sm text-gray-600">Business Owner, Kolkata</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview - Card Style */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Comprehensive Investment Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From equity trading to portfolio management - complete financial services under one roof
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Equity Trading</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Direct equity investments in fundamentally strong companies. SEBI registered advisor with NSE/BSE membership.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Research reports & technical analysis</span>
                </li>
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>SIP in direct stocks</span>
                </li>
              </ul>
              <div className="text-sm font-semibold text-blue-600 mb-2">Min Investment: ₹1 Lakh</div>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6">
                <PieChart className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Mutual Funds</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Diversified mutual fund portfolios across all major AMCs. Systematic investment planning for all risk profiles.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Quarterly tracking & rebalancing</span>
                </li>
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Mobile app for daily tracking</span>
                </li>
              </ul>
              <div className="text-sm font-semibold text-green-600 mb-2">Min Investment: ₹5,000/month</div>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Portfolio Management</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Professional PMS services with customized strategies and dedicated relationship managers.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Top-performing fund managers</span>
                </li>
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Direct equity ownership</span>
                </li>
              </ul>
              <div className="text-sm font-semibold text-purple-600 mb-2">Min Investment: ₹50 Lakhs</div>
            </div>
          </div>
          
          <div className="text-center">
            <Link
              to="/services"
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-10 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 inline-flex items-center shadow-xl"
            >
              View All Services
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA - Strong */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-purple-900 to-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready To Start Your Wealth Journey?
          </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            Book a free consultation with our expert advisors. Let's create a personalized investment strategy for your financial goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/book-consultation"
              className="bg-white text-blue-900 px-10 py-5 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-2xl"
            >
              Book Free Consultation
              <ArrowRight className="ml-2 h-6 w-6" />
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-10 py-5 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center"
            >
              Contact Us
            </Link>
          </div>
          
          <div className="mt-12 pt-8 border-t border-white/20">
            <div className="flex items-center justify-center space-x-8 text-blue-100">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 mr-2" />
                <span>SEBI Registered</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 mr-2" />
                <span>25+ Years Experience</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 mr-2" />
                <span>5000+ Happy Clients</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
